import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  InputGroup,
} from "react-bootstrap";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaSave,
  FaTimes,
} from "react-icons/fa";
import AdminMasterLayout from "../../../layouts/Admin/AdminMasterLayout";
import "../../../assets/css/Admin/AdminProfile/AdminProfile.css";

// ─── Inline Validators ───────────────────────────────────────────────────────

const validateName = (name) => {
  if (!name || name.trim() === "")
    return { isValid: false, error: "Name is required" };
  if (name.trim().length < 3)
    return { isValid: false, error: "Name must be at least 3 characters" };
  if (!/^[a-zA-Z\s]+$/.test(name.trim()))
    return {
      isValid: false,
      error: "Name can only contain letters and spaces",
    };
  return { isValid: true, error: "" };
};

const validateEmail = (email) => {
  if (!email || email.trim() === "")
    return { isValid: false, error: "Email is required" };
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.trim()))
    return { isValid: false, error: "Please enter a valid email address" };
  return { isValid: true, error: "" };
};

const validatePhone = (phone) => {
  if (!phone || phone.trim() === "")
    return { isValid: false, error: "Phone number is required" };
  const phoneRegex = /^(\+92|0)?[0-9]{10,11}$/;
  const cleaned = phone.replace(/[-\s]/g, "");
  if (!phoneRegex.test(cleaned))
    return {
      isValid: false,
      error: "Please enter a valid phone number (e.g. 0300-1234567)",
    };
  return { isValid: true, error: "" };
};

const validatePasswordSimple = (password) => {
  if (!password || password === "")
    return { isValid: false, error: "Password is required" };
  if (password.length < 6)
    return { isValid: false, error: "Password must be at least 6 characters" };
  return { isValid: true, error: "" };
};

const validateConfirmPassword = (password, confirmPassword) => {
  if (!confirmPassword || confirmPassword === "")
    return { isValid: false, error: "Please confirm your password" };
  if (password !== confirmPassword)
    return { isValid: false, error: "Passwords do not match" };
  return { isValid: true, error: "" };
};

// ─────────────────────────────────────────────────────────────────────────────

const AdminProfile = () => {
  // Personal Information State
  const [personalInfo, setPersonalInfo] = useState({
    name: "",
    email: "",
    phone: "",
    currentPassword: "",
  });

  // Change Password State
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Show/Hide Password
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Errors
  const [personalErrors, setPersonalErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});

  // Loading & Success States
  const [isUpdatingPersonal, setIsUpdatingPersonal] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [personalSuccess, setPersonalSuccess] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Handle Personal Info Change
  const handlePersonalChange = (e) => {
    const { name, value } = e.target;
    setPersonalInfo({ ...personalInfo, [name]: value });
    if (personalErrors[name]) {
      setPersonalErrors({ ...personalErrors, [name]: "" });
    }
  };

  // Handle Password Change
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({ ...passwordData, [name]: value });
    if (passwordErrors[name]) {
      setPasswordErrors({ ...passwordErrors, [name]: "" });
    }
  };

  // Toggle Password Visibility
  const togglePasswordVisibility = (field) => {
    setShowPasswords({ ...showPasswords, [field]: !showPasswords[field] });
  };

  // Validate Personal Information
  const validatePersonalInfo = () => {
    const errors = {};

    const nameValidation = validateName(personalInfo.name);
    if (!nameValidation.isValid) errors.name = nameValidation.error;

    const emailValidation = validateEmail(personalInfo.email);
    if (!emailValidation.isValid) errors.email = emailValidation.error;

    const phoneValidation = validatePhone(personalInfo.phone);
    if (!phoneValidation.isValid) errors.phone = phoneValidation.error;

    if (!personalInfo.currentPassword) {
      errors.currentPassword = "Current password is required to update profile";
    }

    setPersonalErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validate Password Change
  const validatePasswordChange = () => {
    const errors = {};

    if (!passwordData.currentPassword) {
      errors.currentPassword = "Current password is required";
    }

    const newPasswordValidation = validatePasswordSimple(
      passwordData.newPassword,
    );
    if (!newPasswordValidation.isValid)
      errors.newPassword = newPasswordValidation.error;

    const confirmValidation = validateConfirmPassword(
      passwordData.newPassword,
      passwordData.confirmPassword,
    );
    if (!confirmValidation.isValid)
      errors.confirmPassword = confirmValidation.error;

    if (
      passwordData.newPassword &&
      passwordData.newPassword === passwordData.currentPassword
    ) {
      errors.newPassword =
        "New password must be different from current password";
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle Personal Info Update
  const handlePersonalUpdate = async (e) => {
    e.preventDefault();
    if (!validatePersonalInfo()) return;

    setIsUpdatingPersonal(true);
    setPersonalSuccess(false);

    try {
      // await axios.put('/api/admin/profile', { ... });
      console.log("Personal info updated:", personalInfo);

      setPersonalSuccess(true);
      setPersonalInfo({ ...personalInfo, currentPassword: "" });
      setTimeout(() => setPersonalSuccess(false), 3000);
    } catch (error) {
      console.error("Error updating profile:", error);
      setPersonalErrors({
        submit:
          "Failed to update profile. Please check your password and try again.",
      });
    } finally {
      setIsUpdatingPersonal(false);
    }
  };

  // Handle Password Update
  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (!validatePasswordChange()) return;

    setIsUpdatingPassword(true);
    setPasswordSuccess(false);

    try {
      // await axios.put('/api/admin/change-password', { ... });
      console.log("Password changed successfully");

      setPasswordSuccess(true);
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (error) {
      console.error("Error changing password:", error);
      setPasswordErrors({
        submit:
          "Failed to change password. Please check your current password.",
      });
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  // Handle Cancel
  const handleCancelPersonal = () => {
    setPersonalInfo({ name: "", email: "", phone: "", currentPassword: "" });
    setPersonalErrors({});
    setPersonalSuccess(false);
  };

  const handleCancelPassword = () => {
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setPasswordErrors({});
    setPasswordSuccess(false);
  };

  return (
    <AdminMasterLayout screenName="dashboard">
      <div className="admin-profile-setting">
        <Container className="admin-profile-setting__container">
          <h2 className="admin-profile-setting__title">Profile Settings</h2>

          <Row>
            {/* Personal Information Section */}
            <Col lg={6} className="mb-4">
              <Card className="admin-profile-setting__card">
                <Card.Header className="admin-profile-setting__card-header">
                  <FaUser className="me-2" />
                  Personal Information
                </Card.Header>
                <Card.Body className="admin-profile-setting__card-body">
                  {personalSuccess && (
                    <Alert
                      variant="success"
                      className="admin-profile-setting__alert"
                    >
                      <strong>Success!</strong> Profile updated successfully.
                    </Alert>
                  )}
                  {personalErrors.submit && (
                    <Alert
                      variant="danger"
                      className="admin-profile-setting__alert"
                    >
                      {personalErrors.submit}
                    </Alert>
                  )}

                  <Form onSubmit={handlePersonalUpdate}>
                    {/* Name */}
                    <Form.Group className="mb-3">
                      <Form.Label className="admin-profile-setting__label">
                        Full Name <span className="text-danger">*</span>
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text className="admin-profile-setting__input-icon">
                          <FaUser />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          name="name"
                          value={personalInfo.name}
                          onChange={handlePersonalChange}
                          isInvalid={!!personalErrors.name}
                          className="admin-profile-setting__input"
                          placeholder="Enter your full name"
                        />
                        <Form.Control.Feedback type="invalid">
                          {personalErrors.name}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>

                    {/* Email */}
                    <Form.Group className="mb-3">
                      <Form.Label className="admin-profile-setting__label">
                        Email Address <span className="text-danger">*</span>
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text className="admin-profile-setting__input-icon">
                          <FaEnvelope />
                        </InputGroup.Text>
                        <Form.Control
                          type="email"
                          name="email"
                          value={personalInfo.email}
                          onChange={handlePersonalChange}
                          isInvalid={!!personalErrors.email}
                          className="admin-profile-setting__input"
                          placeholder="Enter your email"
                        />
                        <Form.Control.Feedback type="invalid">
                          {personalErrors.email}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>

                    {/* Phone */}
                    <Form.Group className="mb-3">
                      <Form.Label className="admin-profile-setting__label">
                        Phone Number <span className="text-danger">*</span>
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text className="admin-profile-setting__input-icon">
                          <FaPhone />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          name="phone"
                          value={personalInfo.phone}
                          onChange={handlePersonalChange}
                          isInvalid={!!personalErrors.phone}
                          className="admin-profile-setting__input"
                          placeholder="0300-1234567"
                        />
                        <Form.Control.Feedback type="invalid">
                          {personalErrors.phone}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>

                    {/* Current Password (for verification) */}
                    <Form.Group className="mb-4">
                      <Form.Label className="admin-profile-setting__label">
                        Current Password <span className="text-danger">*</span>
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text className="admin-profile-setting__input-icon">
                          <FaLock />
                        </InputGroup.Text>
                        <Form.Control
                          type={showPasswords.current ? "text" : "password"}
                          name="currentPassword"
                          value={personalInfo.currentPassword}
                          onChange={handlePersonalChange}
                          isInvalid={!!personalErrors.currentPassword}
                          className="admin-profile-setting__input"
                          placeholder="Enter current password to confirm"
                        />
                        <InputGroup.Text
                          className="admin-profile-setting__eye-icon"
                          onClick={() => togglePasswordVisibility("current")}
                          style={{ cursor: "pointer" }}
                        >
                          {showPasswords.current ? <FaEyeSlash /> : <FaEye />}
                        </InputGroup.Text>
                        <Form.Control.Feedback type="invalid">
                          {personalErrors.currentPassword}
                        </Form.Control.Feedback>
                      </InputGroup>
                      <Form.Text className="text-muted">
                        Required to verify your identity
                      </Form.Text>
                    </Form.Group>

                    {/* Action Buttons */}
                    <div className="admin-profile-setting__actions">
                      <Button
                        variant="outline-secondary"
                        onClick={handleCancelPersonal}
                        disabled={isUpdatingPersonal}
                        className="admin-profile-setting__cancel-btn"
                      >
                        <FaTimes className="me-2" />
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={isUpdatingPersonal}
                        className="admin-profile-setting__save-btn"
                      >
                        {isUpdatingPersonal ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Updating...
                          </>
                        ) : (
                          <>
                            <FaSave className="me-2" />
                            Update Profile
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>

            {/* Change Password Section */}
            <Col lg={6} className="mb-4">
              <Card className="admin-profile-setting__card">
                <Card.Header className="admin-profile-setting__card-header">
                  <FaLock className="me-2" />
                  Change Password
                </Card.Header>
                <Card.Body className="admin-profile-setting__card-body">
                  {passwordSuccess && (
                    <Alert
                      variant="success"
                      className="admin-profile-setting__alert"
                    >
                      <strong>Success!</strong> Password changed successfully.
                    </Alert>
                  )}
                  {passwordErrors.submit && (
                    <Alert
                      variant="danger"
                      className="admin-profile-setting__alert"
                    >
                      {passwordErrors.submit}
                    </Alert>
                  )}

                  <Form onSubmit={handlePasswordUpdate}>
                    {/* Current Password */}
                    <Form.Group className="mb-3">
                      <Form.Label className="admin-profile-setting__label">
                        Current Password <span className="text-danger">*</span>
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text className="admin-profile-setting__input-icon">
                          <FaLock />
                        </InputGroup.Text>
                        <Form.Control
                          type={showPasswords.current ? "text" : "password"}
                          name="currentPassword"
                          value={passwordData.currentPassword}
                          onChange={handlePasswordChange}
                          isInvalid={!!passwordErrors.currentPassword}
                          className="admin-profile-setting__input"
                          placeholder="Enter current password"
                        />
                        <InputGroup.Text
                          className="admin-profile-setting__eye-icon"
                          onClick={() => togglePasswordVisibility("current")}
                          style={{ cursor: "pointer" }}
                        >
                          {showPasswords.current ? <FaEyeSlash /> : <FaEye />}
                        </InputGroup.Text>
                        <Form.Control.Feedback type="invalid">
                          {passwordErrors.currentPassword}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>

                    {/* New Password */}
                    <Form.Group className="mb-3">
                      <Form.Label className="admin-profile-setting__label">
                        New Password <span className="text-danger">*</span>
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text className="admin-profile-setting__input-icon">
                          <FaLock />
                        </InputGroup.Text>
                        <Form.Control
                          type={showPasswords.new ? "text" : "password"}
                          name="newPassword"
                          value={passwordData.newPassword}
                          onChange={handlePasswordChange}
                          isInvalid={!!passwordErrors.newPassword}
                          className="admin-profile-setting__input"
                          placeholder="Enter new password"
                        />
                        <InputGroup.Text
                          className="admin-profile-setting__eye-icon"
                          onClick={() => togglePasswordVisibility("new")}
                          style={{ cursor: "pointer" }}
                        >
                          {showPasswords.new ? <FaEyeSlash /> : <FaEye />}
                        </InputGroup.Text>
                        <Form.Control.Feedback type="invalid">
                          {passwordErrors.newPassword}
                        </Form.Control.Feedback>
                      </InputGroup>
                      <Form.Text className="text-muted">
                        Minimum 6 characters
                      </Form.Text>
                    </Form.Group>

                    {/* Confirm Password */}
                    <Form.Group className="mb-4">
                      <Form.Label className="admin-profile-setting__label">
                        Confirm New Password{" "}
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text className="admin-profile-setting__input-icon">
                          <FaLock />
                        </InputGroup.Text>
                        <Form.Control
                          type={showPasswords.confirm ? "text" : "password"}
                          name="confirmPassword"
                          value={passwordData.confirmPassword}
                          onChange={handlePasswordChange}
                          isInvalid={!!passwordErrors.confirmPassword}
                          className="admin-profile-setting__input"
                          placeholder="Confirm new password"
                        />
                        <InputGroup.Text
                          className="admin-profile-setting__eye-icon"
                          onClick={() => togglePasswordVisibility("confirm")}
                          style={{ cursor: "pointer" }}
                        >
                          {showPasswords.confirm ? <FaEyeSlash /> : <FaEye />}
                        </InputGroup.Text>
                        <Form.Control.Feedback type="invalid">
                          {passwordErrors.confirmPassword}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>

                    {/* Password Requirements */}
                    <div className="admin-profile-setting__password-requirements">
                      <p className="admin-profile-setting__requirements-title">
                        Password Requirements:
                      </p>
                      <ul className="admin-profile-setting__requirements-list">
                        <li>At least 6 characters long</li>
                        <li>Different from current password</li>
                        <li>Should be unique and secure</li>
                      </ul>
                    </div>

                    {/* Action Buttons */}
                    <div className="admin-profile-setting__actions">
                      <Button
                        variant="outline-secondary"
                        onClick={handleCancelPassword}
                        disabled={isUpdatingPassword}
                        className="admin-profile-setting__cancel-btn"
                      >
                        <FaTimes className="me-2" />
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={isUpdatingPassword}
                        className="admin-profile-setting__save-btn"
                      >
                        {isUpdatingPassword ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Changing...
                          </>
                        ) : (
                          <>
                            <FaSave className="me-2" />
                            Change Password
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </AdminMasterLayout>
  );
};

export default AdminProfile;
